/* eslint-disable jsx-a11y/no-static-element-interactions */
import React from 'react';
import { Link } from 'react-router';
import {
  without,
  find,
} from 'lodash';
import moment from 'moment-timezone';
import { CSVLink } from 'react-csv';
import Countdown from 'react-countdown-now';
import classNames from 'classnames';

import { getCSVQuest as getCSV, getKeys } from '../../../../utils/downloadReports';

// Material UI
import { withStyles } from '@material-ui/core/styles';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
} from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';

// Utils
import {
  getSorting,
  stableSort,
  getCompletionStatus,
  getQuestRequirements,
  getQuestRequirementsForDisplay,
  getStreamTimeCompleted,
  keyDistributionStepExists,
  parseLinks,
  isStreamingQuest,
} from '../../../../utils/functions';

// Custom Modal
import CustomLinkModal from './CustomLinkModal';
import RejectVideoModal from './RejectVideoModal';


// Styles
import './QuestDetailsTable.scss';
import {getCurCurrencyOfUnit} from "../../../../utils/util";

const TableHeaderCell = withStyles(theme => ({
  head: {
    color: '#9b9b9b',
    fontWeight: '400',
    letterSpacing: '1.2px',
    fontSize: '14px',
  },
}))(TableCell);

class QuestDetailsTable extends React.Component {
  constructor(props) {
    super(props);
    this.props = props;

    this.state = {
      orderBy: 'dateAccepted',
      order: 'asc',
      showingElements: [],
      usersTab: 0,
    };
  }

  // Determine sort direction for property; set state.order and state.orderBy
  handleRequestSort = (event, property) => {
    const desc = this.state.order === 'desc' && this.state.orderBy === property;
    this.setState({
      order: desc ? 'asc' : 'desc',
      orderBy: property,
    });
  }

  // Determine user's completion status; return element to display
  getCompletionElement = (user) => {
    const { userQuests, quest } = this.props;
    const userQuest = find(userQuests, o => (o.user.id === user.id));
    const {
      timeCompleted, untilDateTime, status,
    } = getCompletionStatus(quest, userQuest, user.timePlaying);

    if (status === 'C') {
      return (
        <TableCell align="center"> <span className="success">C</span></TableCell>
      );
    } else if (status === 'P') {
      return (
        <TableCell align="center"><span className="info">P</span></TableCell>
      );
    } else if (!timeCompleted) {
      return (
        <TableCell align="center">
          <Countdown
            date={untilDateTime}
            daysInHours
            renderer={({
              hours,
              minutes,
              seconds,
              completed,
            }) => {
              if (completed) {
                return (
                  <span className="error">F</span>
                );
              }
              return <span>{hours}:{minutes}:{seconds} left to stream</span>;
            }}
          />
        </TableCell>
      );
    }
    return (
      <TableCell align="center"><span className="error">F</span></TableCell>
    );
  }

  // Open requirements table for user; set state.showingElements
  toggleElementInfo = (elementId) => {
    const { showingElements } = this.state;
    if (showingElements.indexOf(elementId) === -1) {
      this.setState({
        showingElements: [
          ...showingElements,
          elementId,
        ],
      });
    } else {
      this.setState({
        showingElements: [
          ...without(showingElements, elementId),
        ],
      });
    }
  }

  handleComplete = (user, quest, requirement, content) => {
    const {
      id, title, type, subtype,
    } = requirement;

    const reqToUpdate = {
      userId: user.id,
      questId: quest.id,
      reqId: id,
      title,
      type,
      subtype,
      status: 'completed',
      override: true,
      overriddenByUser: this.props.auth.user.id,
    };

    if (content) reqToUpdate.content = content;
    else reqToUpdate.content = 'Admin Override';
    const data = JSON.stringify(reqToUpdate);
    this.props.postCCRequirement(data, this.props.auth.token);
  }

  handleReject = (user, quest, requirement, video, rejectReason) => {
    const {
      id, title, type, subtype,
    } = requirement;
    video.note = rejectReason
    video.status = "invalid"
    const reqToUpdate = {
      userId: user.id,
      questId: quest.id,
      reqId: id,
      title,
      video,
      type,
      subtype,
      status: 'completed',
      override: true,
      overriddenByUser: this.props.auth.user.id,
    };

    const data = JSON.stringify(reqToUpdate);

    this.props.postCCRequirement(data, this.props.auth.token);
  }

  getYoutubeAvgPlayCountValue = (user) => {
    return Math.round(user.user?.connectedAccounts?.youtube?.avgPlayCount);
  }

  // Sort & define user rows based on quest type & sorting state; return elements to display
  getUserRows = (users, questType, platform, keyDistribute) => {
    const { quest, userQuests } = this.props;
    const { order, orderBy, showingElements } = this.state;

    const sortedUsers = stableSort(users, getSorting(order, orderBy));
    const isYoutubeVideoQuest = questType === 'youtube-video';

    if (questType === 'tiered' || questType === 'tiered-one-time' || questType === 'tiered-auto' || questType === 'youtube-video') {
      return sortedUsers.map((user) => {
        let tier = '(?)';
        if (user.tier === 'viewer') {
          tier = 'V';
        } else if (user.tier === 'member') {
          tier = 'M';
        }
        console.log("user", user);

        const youtubeAccount = user.user?.connectedAccounts?.youtube || {};
        const youtubeUsername = youtubeAccount.username || '-';
        const platformUsername = platform === 'twitch' ? user.twitchUsername : 'NA';
        const platformCell = isYoutubeVideoQuest ? (
          <TableCell align="center" className="youtube-identity-cell">
            {youtubeUsername ?
              <Link
                to={`https://www.youtube.com/channel/${youtubeAccount.id}`}
                href={`https://www.youtube.com/channel/${youtubeAccount.id}`}
                target="_blank"
              >
                <Tooltip title="Youtube" placement="top">
                  <span>{youtubeUsername}</span>

                </Tooltip>
                <i style={{ margin: '3px 0px 0px 5px' }} className="fa fa-external-link" />
              </Link>
              :
              '-'
            }
          </TableCell>
        ) : (
          <TableCell align="center">{platformUsername || '-'}</TableCell>
        );
        const userQuest = find(userQuests, o => (o.user.id === user.id));

        // Calculate requirements completion - need to handle Steam achievements separately
        const allRequirements = getQuestRequirements(quest, true);
        const userQuestRequirements = (userQuest && userQuest.requirements) ? userQuest.requirements : [];
        // Count non-Steam achievements completed
        const nonSteamRequirements = allRequirements.filter(req => req.type !== 'submit-steam-achievement');
        const nonSteamCompleted = userQuestRequirements.filter(ur =>
          ur.status === 'completed' &&
          nonSteamRequirements.some(req => req.id === ur.questReqId)
        ).length;
        // Count Steam achievements completed - 统一使用 submit-steam-achievement 类型，基于gameAchievements数组
        let steamAchievementsCompleted = 0;
        const steamRequirements = userQuestRequirements.filter(ur => ur.type === 'submit-steam-achievement');
        steamRequirements.forEach(steamReq => {
          if (steamReq.gameAchievements && Array.isArray(steamReq.gameAchievements)) {
            steamAchievementsCompleted += steamReq.gameAchievements.filter(ga => ga.status === 'completed').length;
          }
        });
        // Total completed requirements
        let sumCompletedRequirements = nonSteamCompleted + steamAchievementsCompleted;
        console.log("sumCompletedRequirements",sumCompletedRequirements)
        // Total requirements count (non-Steam requirements + actual Steam achievements for this user)
        let steamAchievementsTotal = 0;
        steamRequirements.forEach(steamReq => {
          if (steamReq.gameAchievements && Array.isArray(steamReq.gameAchievements)) {
            steamAchievementsTotal += steamReq.gameAchievements.length;
          }
        });
      
        let numberRequirements = nonSteamRequirements.length + steamAchievementsTotal + 1; // +1 for streaming requirement
        if (isYoutubeVideoQuest) {
          numberRequirements -= 1;
        }
        
        const completedStreamTime = getStreamTimeCompleted(quest, user.timePlaying);
        console.log("completedStreamTime",completedStreamTime)
        if (!isYoutubeVideoQuest && completedStreamTime) sumCompletedRequirements += 1;
        const showUserRequirements = showingElements.indexOf(user.username) !== -1;
        const youtubeAvgPlayCount = this.getYoutubeAvgPlayCountValue(user);

        return (
          <>
            <TableRow hover tabIndex={-1} key={user.id}>
              <TableCell align="center">{user.user ? user.user.username : '-'}</TableCell>
              {platformCell}
              <TableCell align="center">{user.paymentAmount ? `${getCurCurrencyOfUnit(quest.region?.currency, user.paymentAmount)}-${tier}` : '0'}</TableCell>
              {isYoutubeVideoQuest ? (
                <TableCell align="center">{youtubeAvgPlayCount}</TableCell>
              ) : (
                <>
                  <TableCell align="center">{user.timePlaying ? user.timePlaying : '0'}</TableCell>
                  <TableCell align="center">{user.averageViewersAtJoin ? Math.floor(user.averageViewersAtJoin) : '0'}</TableCell>
                </>
              )}
              {this.getCompletionElement(user)}
              <TableCell align="center">{user.dateAccepted ? moment(user.dateAccepted).format('MM/DD/YY hh:mm a') : '-'}</TableCell>
              <TableCell align="center">
                <div style={{ display: 'flex' }}>
                  {questType !== 'tiered-auto' && this.props.getEditUserModal(user)}
                  {questType !== 'tiered-auto' && this.props.getEditTierModal(quest, users, user)}
                  {sumCompletedRequirements >= (numberRequirements - 1) &&
                    <Tooltip title="Edit reputation responses" placement="top">
                      <button className="small-btn" onClick={this.props.openCongenialityModal.bind(this, user.id)}>
                        <i className="fa fa-address-card" />
                      </button>
                    </Tooltip>
                  }
                  {numberRequirements > 0 &&
                    <>{sumCompletedRequirements}/{numberRequirements}&nbsp;</>
                  }
                  {!showUserRequirements ?
                    <button className="small-btn" onClick={this.toggleElementInfo.bind(this, user.username)}>+</button>
                    :
                    <button className="small-btn open" onClick={this.toggleElementInfo.bind(this, user.username)}>-</button>
                  }
                </div>
              </TableCell>
            </TableRow>
            {showUserRequirements &&
              this.getUserRequirements(user, userQuest)
            }
          </>
        );
      });
    }

    return sortedUsers.map(user => {
      const userQuest = find(userQuests, o => (o.user.id === user.id));
      
      // Calculate requirements completion - need to handle Steam achievements separately
      const allRequirements = getQuestRequirements(quest, true);
      const userQuestRequirements = (userQuest && userQuest.requirements) ? userQuest.requirements : [];
      
      // Count non-Steam achievements completed
      const nonSteamRequirements = allRequirements.filter(req => req.type !== 'submit-steam-achievement');
      const nonSteamCompleted = userQuestRequirements.filter(ur => 
        ur.status === 'completed' && 
        nonSteamRequirements.some(req => req.id === ur.questReqId)
      ).length;
      
      // Count Steam achievements completed - 统一使用 submit-steam-achievement 类型，基于gameAchievements数组
      let steamAchievementsCompleted = 0;
      const steamRequirements = userQuestRequirements.filter(ur => ur.type === 'submit-steam-achievement');
      steamRequirements.forEach(steamReq => {
        if (steamReq.gameAchievements && Array.isArray(steamReq.gameAchievements)) {
          steamAchievementsCompleted += steamReq.gameAchievements.filter(ga => ga.status === 'completed').length;
        }
      });
      
      // Total completed requirements
      const sumCompletedRequirements = nonSteamCompleted + steamAchievementsCompleted;
      
      // Total requirements count (non-Steam requirements + actual Steam achievements for this user)
      let steamAchievementsTotal = 0;
      steamRequirements.forEach(steamReq => {
        if (steamReq.gameAchievements && Array.isArray(steamReq.gameAchievements)) {
          steamAchievementsTotal += steamReq.gameAchievements.length;
        }
      });
      const numberRequirements = nonSteamRequirements.length + steamAchievementsTotal;
      
      const showUserRequirements = showingElements.indexOf(user.username) !== -1;
      let tier = '(?)';
      if (user.tier === 'viewer') {
        tier = 'V';
      } else if (user.tier === 'member') {
        tier = 'M';
      }
      return (
        <React.Fragment key={user.id}>
          <TableRow hover tabIndex={-1}>
            <TableCell align="center">{user.user ? user.user.username : '-'}</TableCell>
            <TableCell align="center">{user.paymentAmount ? `${getCurCurrencyOfUnit(quest.region?.currency, user.paymentAmount)}-${tier}` : '0'}</TableCell>
            <TableCell align="center">{user.link ? <a href={user.link} target="_blank" rel="noopener noreferrer"> {user.link} </a> : '-'}</TableCell>
            {this.getCompletionElement(user)}
            <TableCell align="center">{user.dateAccepted ? moment(user.dateAccepted).format('MM/DD/YY - hh:mm a') : '-'}</TableCell>
            <TableCell align="center">
              <div style={{ display: 'flex', justifyContent: 'space-evenly' }}>
                {questType !== 'tiered-auto' && this.props.getEditTierModal(quest, users, user)}
                {sumCompletedRequirements >= numberRequirements &&
                  <Tooltip title="Edit reputation responses" placement="top">
                    <button className="small-btn" onClick={this.props.openCongenialityModal.bind(this, user.id)}>
                      <i className="fa fa-address-card" />
                    </button>
                  </Tooltip>
                }
                {numberRequirements > 0 &&
                  <React.Fragment>
                    {sumCompletedRequirements}/{numberRequirements}&nbsp;
                  </React.Fragment>
                }
                {!showUserRequirements ?
                  <button className="small-btn" onClick={this.toggleElementInfo.bind(this, user.username)}>+</button>
                  :
                  <button className="small-btn open" onClick={this.toggleElementInfo.bind(this, user.username)}>-</button>
                }
              </div>
            </TableCell>
          </TableRow>
          {showUserRequirements &&
            this.getUserRequirements(user, userQuest)
          }
        </React.Fragment>
      );
    });
  }

  // Create user requirements table; return element to display
  getUserRequirements = (user, userQuest) => {
    const { quest } = this.props;

    const streamingQuest = isStreamingQuest(quest);
    const fulfilledStreamRequirement = quest.requiredStreamTimeMinutes <= user.timePlaying;

    const requirements = getQuestRequirementsForDisplay(quest, true);

    return (
      <>
        <TableRow>
          <TableCell colSpan="8">
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeaderCell align="left"> Mark Complete </TableHeaderCell>
                  <TableHeaderCell align="left"> Requirement </TableHeaderCell>
                  <TableHeaderCell align="left"> Reason </TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(requirements && requirements.length > 0) && requirements.map((r) => {
                  const { type, subtype, approvalRequired } = r;

                  // Find user quest
                  const userQuestRequirements = (userQuest && userQuest.requirements) ? userQuest.requirements : [];
                  const ur = find(userQuestRequirements, o => o.questReqId === r.id);
                  const views = ur && ur.views ? ur.views : 'n/a';

                  // multi-video-submission: 每个视频一行
                  if (ur && type === 'multi-video-submission' && Array.isArray(ur?.videos)) {
                    return ur.videos.map((video, idx) => {
                      const statusClass = classNames({
                        success: video.status === 'valid',
                        error: video.status === 'rejected',
                        warning: video.status !== 'valid' && video.status !== 'rejected',
                      });

                      const Buttons = (
                        <TableCell>

                          <RejectVideoModal
                            videoUrl={video.url}
                            onReject={reason => this.handleReject(user, quest, r, video, reason)}
                          />
                        </TableCell>
                      );

                      // 备注
                      const noteHtml = <TableCell>{video.note ? <span className={statusClass}><i>{video.note}</i></span> : null}</TableCell>;

                      return (
                        <TableRow key={video.videoId || idx}>
                          {Buttons}
                          <TableCell>
                            <span className={statusClass}>
                              Youtube Link:&nbsp;<Link href={video.url} target="_blank">{video.url}</Link>
                            </span>
                          </TableCell>
                          {noteHtml}
                        </TableRow>
                      );
                    });
                  }

                  // 其它类型保持原有逻辑
                  const statusClass = classNames({
                    success: ur && (ur.status === 'completed'),
                    error: ur && (ur.status === 'rejected' || ur.status === 'not_completed'),
                    warning: (ur && ur.status === 'not_completed_in_time') || (!ur || (ur && (ur.status !== 'completed' && ur.status !== 'rejected' && ur.status !== 'not_completed' && ur.status !== 'not_completed_in_time'))),
                  });

                  // Format link-based submissions
                  let linkHTML = <React.Fragment />;

                  if (ur && (ur.type === 'submit-tweet' || ur.type === 'submit-link')) {
                    if (!ur.content.toLowerCase().startsWith('http')) {
                      linkHTML = <Link href={`https://${ur.content.toLowerCase()}`} target="_blank">{ur.content}</Link>;
                    } else if (ur.content) {
                      linkHTML = <Link href={`${ur.content.toLowerCase()}`} target="_blank">{ur.content}</Link>;
                    }
                  }

                  let text = '';
                  if (ur) {
                    if (type === 'submit-tweet') {
                      text = <TableCell><span className={statusClass}>Twitter Link:&nbsp;{linkHTML}</span></TableCell>;
                    } else if (type === 'submit-link' && (subtype && subtype === 'vod')) {
                      text = (
                        <TableCell>
                          <span className={statusClass}>{'VOD Link:'}&nbsp;{linkHTML}{' - Views: '}{views}</span>
                          {(ur.status === 'completed') ? (
                            <button className="small-btn" style={{ margin: '0 0 0 5px' }} onClick={this.props.openVODModals.bind(this, user.id, ur.questReqId)}>
                              <i className="fa fa-address-card" />
                            </button>
                          ) : null}
                        </TableCell>
                      );
                    } else if (type === 'multi-video-submission') {
                      text = <TableCell><span className={statusClass}>Youtube Link:&nbsp;{ur.content}</span></TableCell>;
                    } else if (type === 'submit-link') {
                      text = <TableCell><span className={statusClass}>{subtype === 'vod' ? 'VOD Link:' : 'Clip Link:'}&nbsp;{linkHTML}</span></TableCell>;
                    } else if (type === 'submit-video-youtube') {
                      text = <TableCell>
                        <div className={statusClass}>Youtube Video Link:&nbsp;{ur.content}</div>
                        <div>
                          <span className={statusClass}>Views: {ur.views}</span> &nbsp;
                          <span className={statusClass}>Likes: {ur.likes}</span> &nbsp;
                          <span className={statusClass}>Comments: {ur.comments}</span>
                        </div>
                      </TableCell>;
                    } else if (type === 'submit-text' || type === 'submit-text-optional') {
                      text = <TableCell><span className={statusClass}>{r.title}: {parseLinks(ur.content)}</span></TableCell>;
                    } else if (type === 'submit-steam-achievement' || type === 'steam-achievement') {
                      // Skip individual achievement rendering here, will be handled separately below
                      return null;
                    } else if (approvalRequired) {
                      text = <TableCell><span className={statusClass}>{`Checkbox submitted: ${r.title}`}</span></TableCell>;
                    }
                  } else if (type === 'multi-video-submission') {
                    text = <TableCell><span className="warning"><i>Youtube Link Pending</i></span></TableCell>;
                  } else if (type === 'submit-tweet') {
                    text = <TableCell><span className="warning"><i>Twitter Link Pending</i></span></TableCell>;
                  } else if (type === 'submit-link') {
                    text = <TableCell><span className="warning"><i>{subtype === 'vod' ? 'VOD Link Pending' : 'Clip Link Pending'}</i></span></TableCell>;
                  } else if (type === 'submit-video-youtube') {
                    text = <TableCell><span className="warning"><i> Video Link Pending</i></span></TableCell>;
                  } else if (type === 'submit-text' || type === 'submit-text-optional') {
                    text = <TableCell><span className="warning"><i>Text Submission Pending</i></span></TableCell>;
                  } else if (type === 'submit-steam-achievement' || type === 'steam-achievement') {
                    // Skip individual achievement rendering here for pending state, will be handled separately below
                    return null;
                  } else if (approvalRequired) {
                    text = <TableCell><span className="warning">Checkbox pending</span></TableCell>;
                  }

                  // Create table cell for note
                  let noteHtml = <TableCell />;
                  if (ur && ur.note) {
                    noteHtml = <TableCell><span className={statusClass}><i>{ur.note}</i></span></TableCell>;
                  } else if ((type === 'submit-steam-achievement' || type === 'steam-achievement') && ur && ur.status === 'not_completed_in_time') {
                    noteHtml = <TableCell><span className="error"><i>Not within the quest period.</i></span></TableCell>;
                  }

                  // Create table cell for admin override button (if applicable)
                  let completeButton = <TableCell />;
                  if (type !== 'submit-steam-achievement' && type !== 'steam-achievement' && (!ur || (ur && ur.status !== 'completed'))) {
                    completeButton = (
                      <TableCell>
                        <CustomLinkModal
                          completeSelected={link => this.handleComplete(user, quest, r, link)}
                          selectedCheckboxesLength={1}
                          {...(ur && ur.link ? { existingLink: ur.link } : {})}
                        />

                      </TableCell>
                    );
                  }

                  return (
                    <TableRow>
                      {completeButton}
                      {text}
                      {noteHtml}
                    </TableRow>
                  );
                }).filter(row => row !== null)}

                {/* Render individual Steam achievements from gameAchievements array */}
                {(() => {
                  const userQuestRequirements = (userQuest && userQuest.requirements) ? userQuest.requirements : [];
                  const steamRequirements = userQuestRequirements.filter(req =>
                    req.type === 'submit-steam-achievement'
                  );

                  // 展示每个Steam需求下的具体成就
                  return steamRequirements.flatMap((steamRequirement, reqIndex) => {
                    if (!steamRequirement.gameAchievements || !Array.isArray(steamRequirement.gameAchievements)) {
                      return [];
                    }

                    return steamRequirement.gameAchievements.map((gameAchievement, achIndex) => {
                      const { status, achievementId, completedAt } = gameAchievement;
                      const { displayName, description } = gameAchievement;

                      // 根据status和completedAt判断状态
                      let achievementStatus = 'not_completed';
                      if (status === 'completed') {
                        achievementStatus = 'completed';
                      } else if (completedAt !== null && completedAt !== undefined) {
                        achievementStatus = 'not_completed_in_time';
                      } else {
                        achievementStatus = 'not_completed';
                      }

                      // 确定状态样式
                      const statusClass = classNames({
                        success: achievementStatus === 'completed',
                        error: achievementStatus === 'not_completed',
                        warning: achievementStatus === 'not_completed_in_time',
                      });

                      // 状态文本
                      let statusText = '';
                      if (achievementStatus === 'completed') {
                        statusText = 'Completed';
                      } else if (achievementStatus === 'not_completed_in_time') {
                        statusText = 'Not completed in time';
                      } else {
                        statusText = 'Not completed';
                      }

                      // 显示内容
                      const displayContent = `GameAchievement ${displayName || 'Unknown Achievement'}`;
                      const achievementDescription = description || 'No description available';

                      return (
                        <TableRow key={`${steamRequirement.questReqId}-${achievementId}-${reqIndex}-${achIndex}`}>
                          <TableCell>{/* No Mark Complete button for auto-judged achievements */}</TableCell>
                          <TableCell>
                            <span className={statusClass} style={{ fontSize: '14px', fontWeight: 'normal' }}>
                              {displayContent}
                            </span>
                            <br />
                            <small style={{ color: '#666', fontSize: '12px' }}>
                              {achievementDescription}
                            </small>
                          </TableCell>
                          <TableCell>
                            <span className={statusClass} style={{ fontSize: '14px', fontWeight: 'normal' }}>
                              {statusText}
                            </span>
                          </TableCell>
                        </TableRow>
                      );
                    });
                  });
                })()}
                {streamingQuest && (fulfilledStreamRequirement ? (
                  <TableRow>
                    <TableCell />
                    <TableCell>
                      <span className="success">Streamed for {quest.requiredStreamTimeMinutes} minutes.</span>
                    </TableCell>
                    <TableCell />
                  </TableRow>
                ) : (
                  <TableRow>
                    <TableCell>
                      {this.props.getEditUserModal(user)}
                    </TableCell>
                    <TableCell>
                      <span className="warning">Streamed for {quest.requiredStreamTimeMinutes} minutes.</span>
                    </TableCell>
                    <TableCell />
                  </TableRow>
                ))}

                {/* Promo link */}
                {(userQuest.promoLink && (
                  <Table >
                    <TableHead>
                      <TableRow>
                        <TableHeaderCell colSpan="8" align="left"> Promo Link </TableHeaderCell>
                        <TableHeaderCell colSpan="8" align="left"> Clicks</TableHeaderCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell colSpan="8"><a href={`http://${userQuest.promoLink}`} target="_blank" rel="noopener noreferrer">{userQuest.promoLink}</a></TableCell>
                        <TableCell colSpan="8">{userQuest.promoLinkClicks}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                ))}
              </TableBody>
            </Table>
          </TableCell>
        </TableRow>
        {userQuest.leftQuestReason &&
          <TableRow>
            <TableCell colSpan="8">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell align="left">Reason for Leaving</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow style={{ padding: '0.67em' }}>
                    <TableCell colSpan="8">
                      {userQuest.leftQuestReason}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableCell>
          </TableRow>
        }
      </>
    );
  }

  // Define table headers based on quest type; return array of headers
  getTableHeaders = (questType, platform, keyDistribute) => {
    const { usersTab } = this.state;
    console.log("questType", questType)


    let tableHeaders = [];

    switch (questType) {
      case 'tiered':
      case 'tiered-one-time':
      case 'tiered-auto':
        tableHeaders = [
          {
            id: 'username', numeric: false, disablePadding: true, label: 'Noiz',
          },
          {
            id: `${platform}Username`, numeric: false, disablePadding: true, label: platform.charAt(0).toUpperCase() + platform.slice(1),
          },
          {
            id: 'paymentAmount', numeric: true, disablePadding: true, label: 'Payment Amount',
          },
          {
            id: 'averageViewers', numeric: true, disablePadding: true, label: 'Average Viewers',
          },
          {
            id: 'completionStatus', numeric: false, disablePadding: true, label: 'Status',
          },
          {
            id: 'dateAccepted', numeric: false, disablePadding: true, label: 'Date Accepted',
          },
          {
            id: 'actions', numeric: false, disablePadding: true, label: 'Actions',
          },
        ];
        break;
      case 'youtube-video':
        tableHeaders = [
          {
            id: 'username', numeric: false, disablePadding: true, label: 'Noiz',
          },
          {
            id: `youtubeUsername`, numeric: false, disablePadding: true, label: 'YouTube',
          },
          {
            id: 'paymentAmount', numeric: true, disablePadding: true, label: 'Payment Amount',
          },
          {
            id: 'youtubeAvgPlayCount', numeric: true, disablePadding: true, label: 'YouTube Avg Play Count',
          },
          {
            id: 'completionStatus', numeric: false, disablePadding: true, label: 'Status',
          },
          {
            id: 'dateAccepted', numeric: false, disablePadding: true, label: 'Date Accepted',
          },
          {
            id: 'actions', numeric: false, disablePadding: true, label: 'Actions',
          },
        ];
        break;
      default:
        tableHeaders = [
          {
            id: 'username', numeric: false, disablePadding: false, label: 'Noiz',
          },
          {
            id: 'paymentAmount', numeric: true, disablePadding: false, label: 'Payment Amount',
          },
          {
            id: 'link', numeric: false, disablePadding: false, label: 'Link',
          },
          {
            id: 'completionStatus', numeric: false, disablePadding: false, label: 'Status',
          },
          {
            id: 'dateAccepted', numeric: false, disablePadding: false, label: 'Date Accepted',
          },
          {
            id: 'actions', numeric: false, disablePadding: false, label: 'Actions',
          },
        ];
        break;
    }
    if (usersTab === 0 && keyDistribute) {
      tableHeaders.push({
        id: 'gameKey', numeric: false, disablePadding: true, label: '',
      });
    }

    return tableHeaders;
  }

  // Define data for csv download; return object with headers and data

  renderFirstRow = (approvedUsers, leftQuestUsers) => {
    const { usersTab } = this.state;
    return (
      <div className="Global__subTabs" style={{ padding: '0px 30px 10px 30px' }}>
        <div className={classNames('Global__subTab', { active: usersTab === 0 })} onClick={() => this.setState({ usersTab: 0 })} aria-hidden>
          In Quest ({approvedUsers.length})
        </div>
        {(leftQuestUsers.length) ? (
          <div className={classNames('Global__subTab', { active: usersTab === 1 })} onClick={() => this.setState({ usersTab: 1 })} aria-hidden>
            Left Quest ({leftQuestUsers.length})
          </div>
        ) : null}
      </div>
    );
  }

  render() {
    const {
      questType, users, quest, userQuests,
    } = this.props;
    const { twitchAccountRequired, youtubeAccountRequired } = quest;
    const { usersTab } = this.state;

    let platform = 'twitch';
    if (twitchAccountRequired) {
      platform = 'twitch';
    }

    if (youtubeAccountRequired) {
      platform = 'youtube';
    }



    users.forEach((user) => {
      const userQuest = find(userQuests, o => (o.user.id === user.id));
      user.completionStatus = getCompletionStatus(quest, userQuest, user.timePlaying);
      if (userQuest.chatDetails) { user.chatDetails = userQuest.chatDetails; }
    });

    const approvedUsers = users.filter(user => user.status !== 'leftQuest');
    const failedUsers = approvedUsers.filter(user => user.completionStatus.status === 'F');
    const leftQuestUsers = users.filter(user => user.status === 'leftQuest');
    const currentTabUsers = usersTab === 0 ? approvedUsers : leftQuestUsers;

    const keyDistribute = quest.keyRequired && keyDistributionStepExists(quest);

    const csv = getCSV(currentTabUsers, platform, quest, userQuests);
    const keysCSV = getKeys([].concat(failedUsers, leftQuestUsers), platform, quest);

    const createSortHandler = property => (event) => {
      this.handleRequestSort(event, property);
    };

    return (
      <section className="ToggleTable">
        {currentTabUsers.length ? (
          <React.Fragment>
            <section className="ToggleTable__header">
              <h3 className="ToggleTable__header-title">Users: <span>{currentTabUsers?.length}</span></h3>
              {(currentTabUsers?.length) ?
                (
                  <div className="ToggleTable__header-buttons">
                    <Tooltip title="Report CSV" placement="top">
                      <CSVLink
                        data={csv.dataCSV}
                        headers={csv.headers}
                        filename={(quest && quest.game) ? `${quest.game.name} - ${quest.title}.csv` : 'report.csv'}
                      >
                        <i className="fa fa-download" />
                      </CSVLink>
                    </Tooltip>
                    {quest.keyRequired &&
                      <Tooltip title="Keys to Revoke" placement="top">
                        <CSVLink
                          data={keysCSV.dataCSV}
                          headers={keysCSV.headers}
                          filename={quest && quest.game ? `${quest.game.name} - ${quest.title} - KeysToRevoke.csv` : 'keysToRevoke.csv'}
                        >
                          <i className="fa fa-key" />
                        </CSVLink>
                      </Tooltip>
                    }
                  </div>
                ) : null}
            </section>
            {this.renderFirstRow(approvedUsers, leftQuestUsers)}
            <div className="table-scroll-container">
            <Table>
              <TableHead>
                <TableRow>
                  {this.getTableHeaders(questType, platform, keyDistribute).map(headCell => (
                    <TableHeaderCell
                      key={headCell.id}
                      align="center"
                      padding={headCell.disablePadding ? 'none' : 'normal'}
                      sortDirection="asc"
                    >
                      <TableSortLabel
                        active={this.state.orderBy === headCell.id}
                        direction={this.state.order}
                        onClick={createSortHandler(headCell.id)}
                        IconComponent={ArrowDropDownIcon}
                        style={{ marginLeft: '30px' }}
                      >
                        {headCell.label}
                        {this.state.orderBy === headCell.id ? (
                          <span style={{ display: 'none' }}>
                            {this.state.order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                          </span>
                        ) : null}
                      </TableSortLabel>
                    </TableHeaderCell>))}
                </TableRow>
              </TableHead>
              <TableBody>
                {this.getUserRows(currentTabUsers, questType, platform, keyDistribute)}
              </TableBody>
            </Table>
            </div>
          </React.Fragment>
        ) : (<div className="NoQuestTiered">No one has joined yet.</div>)}
      </section>
    );
  }
}

QuestDetailsTable.propTypes = {
};

export default QuestDetailsTable;
